import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BatchResponse, SeedResponse, CreateBatchRequest, FilterState } from '../types';
import {
  listBatches,
  listSeeds,
  createBatch,
  runBatch,
  deleteBatch,
  listTags,
  fetchAllSeeds,
} from '../api';
import { useScope } from '../scopeContext';
import ProjectPicker from './ProjectPicker';
import TagFilterBar from './TagFilterBar';
import CreateBatchModal from './CreateBatchModal';

interface BatchFormData {
  name: string;
  seed_ids: string[];
  run_mode: 'sequential' | 'parallel';
  max_parallel: number;
  autorun: boolean;
  repetition_count: number;
  tags_csv: string;
  project_id: string | null;
}

const emptyBatchForm: BatchFormData = {
  name: '',
  seed_ids: [],
  run_mode: 'parallel',
  max_parallel: 5,
  autorun: true,
  repetition_count: 1,
  tags_csv: '',
  project_id: null,
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const BatchManager: React.FC = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 100;
  const SCROLL_BOTTOM_THRESHOLD = 300;
  const [batches, setBatches] = useState<BatchResponse[]>([]);
  const [seeds, setSeeds] = useState<SeedResponse[]>([]);
  const [seedTotal, setSeedTotal] = useState(0);
  const [seedLoadingMore, setSeedLoadingMore] = useState(false);
  const [seedHasMore, setSeedHasMore] = useState(false);
  const [selectingAllFilteredSeeds, setSelectingAllFilteredSeeds] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<BatchFormData>(emptyBatchForm);
  const [filter, setFilter] = useState<FilterState>({
    text_search: '',
    include_tags: [],
    exclude_tags: [],
    untagged: 'all',
  });
  const [seedFilter, setSeedFilter] = useState<FilterState>({
    text_search: '',
    include_tags: [],
    exclude_tags: [],
    untagged: 'all',
  });
  const [tags, setTags] = useState<string[]>([]);
  const { scope, refreshProjects } = useScope();

  const fetchBatches = useCallback(
    async (offset: number, reset: boolean) => {
      if (reset) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }
      try {
        const response = await listBatches(PAGE_SIZE, offset, undefined, filter, scope);
        setTotal(response.total);
        setHasMore(offset + response.batches.length < response.total);
        setBatches((prev) => (reset ? response.batches : [...prev, ...response.batches]));
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to fetch batches';
        setError(message);
      } finally {
        if (reset) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [PAGE_SIZE, filter, scope]
  );

  const fetchSeeds = useCallback(
    async (offset: number, reset: boolean) => {
      if (!reset) {
        setSeedLoadingMore(true);
      }
      try {
        const response = await listSeeds(PAGE_SIZE, offset, seedFilter, scope);
        setSeedTotal(response.total);
        setSeedHasMore(offset + response.seeds.length < response.total);
        setSeeds((prev) => (reset ? response.seeds : [...prev, ...response.seeds]));
      } catch (err: unknown) {
        console.error('Failed to fetch seeds:', err);
      } finally {
        if (!reset) {
          setSeedLoadingMore(false);
        }
      }
    },
    [PAGE_SIZE, seedFilter, scope]
  );

  useEffect(() => {
    void fetchBatches(0, true);
  }, [fetchBatches, filter]);
  useEffect(() => {
    void fetchSeeds(0, true);
  }, [fetchSeeds, seedFilter]);
  useEffect(() => {
    const onScroll = () => {
      if (loading || loadingMore || !hasMore || showForm) {
        return;
      }
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - SCROLL_BOTTOM_THRESHOLD;
      if (scrolledToBottom) {
        void fetchBatches(batches.length, false);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [batches.length, fetchBatches, hasMore, loading, loadingMore, showForm]);
  useEffect(() => {
    void listTags(scope).then((res) => setTags(res.tags)).catch(() => setTags([]));
  }, [scope]);

  // Poll for running batches
  useEffect(() => {
    const hasRunning = batches.some((b) => b.status === 'running');
    if (!hasRunning) return;

    const interval = setInterval(() => {
      void fetchBatches(0, true);
    }, 3000);
    return () => clearInterval(interval);
  }, [batches, fetchBatches]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'max_parallel'
          ? Math.min(100, Math.max(1, parseInt(value, 10) || 1))
          : name === 'repetition_count'
          ? Math.min(50, Math.max(1, parseInt(value, 10) || 1))
          : value,
    }));
  };

  const handleSeedSelection = (seedId: string) => {
    setFormData((prev) => ({
      ...prev,
      seed_ids: prev.seed_ids.includes(seedId)
        ? prev.seed_ids.filter((id) => id !== seedId)
        : [...prev.seed_ids, seedId],
    }));
  };

  const handleUnselectAllSeeds = () => {
    setFormData((prev) => ({ ...prev, seed_ids: [] }));
  };

  const handleSelectAllFilteredSeeds = async () => {
    setSelectingAllFilteredSeeds(true);
    try {
      const allFilteredSeeds = await fetchAllSeeds(seedFilter, scope);
      const filteredIds = allFilteredSeeds.map((seed) => seed.seed_id);
      const deduplicated = Array.from(new Set([...formData.seed_ids, ...filteredIds]));
      setFormData((prev) => ({
        ...prev,
        seed_ids: deduplicated,
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to select filtered seeds';
      setError(message);
    } finally {
      setSelectingAllFilteredSeeds(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.seed_ids.length === 0) {
      setError('Please select at least one seed');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const createData: CreateBatchRequest = {
        name: formData.name,
        seed_ids: formData.seed_ids,
        run_mode: formData.run_mode,
        max_parallel: formData.run_mode === 'parallel' ? formData.max_parallel : undefined,
        autorun: formData.autorun,
        repetition_count: formData.repetition_count,
        tags: formData.tags_csv.split(',').map((item) => item.trim()).filter(Boolean),
        project_id: formData.project_id ?? undefined,
      };
      await createBatch(createData);
      setShowForm(false);
      setFormData(emptyBatchForm);
      refreshProjects();
      await fetchBatches(0, true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create batch';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(emptyBatchForm);
  };

  const handleRun = async (batchId: string) => {
    setLoading(true);
    setError('');
    try {
      await runBatch(batchId);
      await fetchBatches(0, true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to run batch';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (batchId: string) => {
    if (!confirm('Delete this batch?')) return;
    setLoading(true);
    setError('');
    try {
      await deleteBatch(batchId);
      await fetchBatches(0, true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete batch';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#4caf50';
      case 'running':
        return '#2196f3';
      case 'failed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <div>
      <ProjectPicker />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>Batches ({total})</h2>
        <button
          className="button button-primary"
          onClick={() => {
            const now = new Date();
            const defaultName = `Batch ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
            setFormData({ ...emptyBatchForm, name: defaultName, project_id: scope.project_id });
            setShowForm(true);
          }}
          disabled={loading || seeds.length === 0 || !scope.project_id}
        >
          + New Batch
        </button>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#fff0f0',
            color: '#8b0000',
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      {seeds.length === 0 && !showForm && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#fff9e6',
            color: '#996600',
            borderRadius: 6,
          }}
        >
          Create some seeds first before creating a batch.
        </div>
      )}

      <TagFilterBar tags={tags} value={filter} onChange={setFilter} />

      {showForm && (
        <CreateBatchModal
          open={showForm}
          loading={loading}
          seedFilter={seedFilter}
          availableTags={tags}
          seeds={seeds}
          selectedSeedIds={formData.seed_ids}
          name={formData.name}
          runMode={formData.run_mode}
          maxParallel={formData.max_parallel}
          autorun={formData.autorun}
          repetitionCount={formData.repetition_count}
          tagsCsv={formData.tags_csv}
          projectId={formData.project_id}
          onProjectChange={(id) => setFormData((prev) => ({ ...prev, project_id: id }))}
          onFilterChange={setSeedFilter}
          onToggleSeed={handleSeedSelection}
          onLoadMoreSeeds={() => void fetchSeeds(seeds.length, false)}
          seedHasMore={seedHasMore}
          seedLoadingMore={seedLoadingMore}
          seedTotal={seedTotal}
          onSelectAllFiltered={handleSelectAllFilteredSeeds}
          selectingAllFiltered={selectingAllFilteredSeeds}
          onUnselectAll={handleUnselectAllSeeds}
          onClose={handleCancel}
          onSubmit={handleSubmit}
          onFieldChange={handleInputChange}
        />
      )}

      {loading && !showForm && <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>}

      {!loading && batches.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No batches found. Create one to run self-play sessions.
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {batches.map((batch) => (
          <div
            key={batch.batch_id}
            style={{
              background: '#fff',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{batch.name}</span>
                  <span
                    style={{
                      padding: '2px 8px',
                      fontSize: 11,
                      borderRadius: 10,
                      background: getStatusColor(batch.status),
                      color: 'white',
                    }}
                  >
                    {batch.status}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  Seeds: {batch.seed_count} | Completed: {batch.completed_count ?? 0} |
                  Failed: {batch.failed_count ?? 0}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Mode: {batch.run_mode} | Autorun: {batch.autorun ? 'Yes' : 'No'}
                  {batch.repetition_count > 1 && ` | Reps: ${batch.repetition_count}`}
                  {batch.tags && batch.tags.length > 0 && ` | Tags: ${batch.tags.join(', ')}`}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Created: {formatDate(batch.created_at)}
                  {batch.started_at && ` | Started: ${formatDate(batch.started_at)}`}
                  {batch.ended_at && ` | Ended: ${formatDate(batch.ended_at)}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {batch.status === 'pending' && (
                  <button
                    onClick={() => handleRun(batch.batch_id)}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      background: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    Run
                  </button>
                )}
                {(batch.status === 'completed' || batch.status === 'failed') && (
                  <button
                    onClick={() => navigate(`/batches/${batch.batch_id}`)}
                    style={{
                      padding: '4px 8px',
                      fontSize: 12,
                      background: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    View Results
                  </button>
                )}
                <button
                  onClick={() => handleDelete(batch.batch_id)}
                  disabled={batch.status === 'running'}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12,
                    background: batch.status === 'running' ? '#ccc' : '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: batch.status === 'running' ? 'not-allowed' : 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {loadingMore && (
        <div style={{ padding: 12, textAlign: 'center', color: '#666' }}>Loading more...</div>
      )}
    </div>
  );
};

export default BatchManager;
